import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/app/components/ui/tooltip';
import type { ComplianceOpenNonConformance } from '@/lib/types';

const STATUS_LABEL: Record<string, string> = {
  compliant: 'Compliant',
  partially_compliant: 'Partial',
  non_compliant: 'Non-Conformant',
  not_applicable: 'N/A',
  not_assessed: 'Not Assessed',
};

function NonConformanceRow({ nc }: { nc: ComplianceOpenNonConformance }) {
  const isPartial = nc.effective_status === 'partially_compliant';
  const Icon = isPartial ? AlertTriangle : XCircle;
  const tone = isPartial ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-100 px-4 py-3">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">
            {nc.clause_ref}
          </span>
          <span className="truncate text-sm text-gray-700">{nc.clause_title}</span>
          <Badge
            className={
              isPartial
                ? 'bg-amber-100 text-amber-800'
                : 'bg-red-100 text-red-800'
            }
          >
            {STATUS_LABEL[nc.effective_status] ?? nc.effective_status}
          </Badge>
          {nc.disagrees && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className="cursor-help bg-purple-100 text-purple-800">
                  Engine vs recorded
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Engine assessed{' '}
                {STATUS_LABEL[nc.engine_status] ?? nc.engine_status}; recorded
                position is {STATUS_LABEL[nc.recorded_status] ?? nc.recorded_status}.
                The headline score reflects the recorded status.
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {nc.reason && (
          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{nc.reason}</p>
        )}
      </div>
    </div>
  );
}

export function OpenNonConformancesCard({
  count,
  items,
  limit = 8,
}: {
  count: number;
  items: ComplianceOpenNonConformance[];
  limit?: number;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Open Non-Conformances
            </h2>
            <Badge
              className={
                count > 0
                  ? 'bg-red-100 text-red-800'
                  : 'bg-green-100 text-green-800'
              }
            >
              {count}
            </Badge>
          </div>
          <Link
            to="/compliance/dashboard"
            className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            View clauses
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {count === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">
            No open non-conformances — the assessment engine and recorded
            positions agree.
          </p>
        ) : (
          <div className="space-y-3">
            {items.slice(0, limit).map((nc) => (
              <NonConformanceRow key={nc.clause_ref} nc={nc} />
            ))}
            {items.length > limit && (
              <p className="text-center text-xs text-gray-400">
                +{items.length - limit} more
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
